/**
 * ASCII Box Drawing Utilities
 *
 * A reusable library for creating properly aligned ASCII art boxes,
 * panels, and window frames for the PersonaOS retro UI.
 */

// Box drawing character sets
export const BoxChars = {
  // Double-line box (═ ║ ╔ ╗ ╚ ╝)
  double: {
    topLeft: '╔',
    topRight: '╗',
    bottomLeft: '╚',
    bottomRight: '╝',
    horizontal: '═',
    vertical: '║',
  },
  // Single-line box (─ │ ┌ ┐ └ ┘)
  single: {
    topLeft: '┌',
    topRight: '┐',
    bottomLeft: '└',
    bottomRight: '┘',
    horizontal: '─',
    vertical: '│',
  },
  // Heavy/thick box
  heavy: {
    topLeft: '┏',
    topRight: '┓',
    bottomLeft: '┗',
    bottomRight: '┛',
    horizontal: '━',
    vertical: '┃',
  },
  // Rounded corners
  rounded: {
    topLeft: '╭',
    topRight: '╮',
    bottomLeft: '╰',
    bottomRight: '╯',
    horizontal: '─',
    vertical: '│',
  },
} as const

export type BoxStyle = keyof typeof BoxChars

export interface BoxOptions {
  /** Width of the box interior (not including borders) */
  width: number
  /** Box border style */
  style?: BoxStyle
  /** Padding inside the box (default: 0) */
  padding?: number
  /** Text alignment for content lines */
  align?: 'left' | 'center' | 'right'
}

/**
 * Center a string within a given width
 */
export function centerText(text: string, width: number): string {
  const textLen = text.length
  if (textLen >= width) return text.slice(0, width)

  const totalPadding = width - textLen
  const leftPad = Math.floor(totalPadding / 2)
  const rightPad = totalPadding - leftPad

  return ' '.repeat(leftPad) + text + ' '.repeat(rightPad)
}

/**
 * Left-align a string within a given width
 */
export function leftAlign(text: string, width: number): string {
  const textLen = text.length
  if (textLen >= width) return text.slice(0, width)
  return text + ' '.repeat(width - textLen)
}

/**
 * Right-align a string within a given width
 */
export function rightAlign(text: string, width: number): string {
  const textLen = text.length
  if (textLen >= width) return text.slice(0, width)
  return ' '.repeat(width - textLen) + text
}

/**
 * Align text based on alignment option
 */
export function alignText(text: string, width: number, align: 'left' | 'center' | 'right'): string {
  switch (align) {
    case 'center': return centerText(text, width)
    case 'right': return rightAlign(text, width)
    default: return leftAlign(text, width)
  }
}

/**
 * Create the top border of a box
 */
export function boxTop(width: number, style: BoxStyle = 'double'): string {
  const chars = BoxChars[style]
  return chars.topLeft + chars.horizontal.repeat(width) + chars.topRight
}

/**
 * Create the bottom border of a box
 */
export function boxBottom(width: number, style: BoxStyle = 'double'): string {
  const chars = BoxChars[style]
  return chars.bottomLeft + chars.horizontal.repeat(width) + chars.bottomRight
}

/**
 * Create a content row within a box
 */
export function boxRow(content: string, width: number, style: BoxStyle = 'double', align: 'left' | 'center' | 'right' = 'left'): string {
  const chars = BoxChars[style]
  const alignedContent = alignText(content, width, align)
  return chars.vertical + alignedContent + chars.vertical
}

/**
 * Create an empty row within a box
 */
export function boxEmptyRow(width: number, style: BoxStyle = 'double'): string {
  const chars = BoxChars[style]
  return chars.vertical + ' '.repeat(width) + chars.vertical
}

/**
 * Create a horizontal divider row within a box
 */
export function boxDivider(width: number, style: BoxStyle = 'double'): string {
  const chars = BoxChars[style]
  // Use connecting characters for dividers
  const connectors = {
    double: { left: '╠', right: '╣' },
    single: { left: '├', right: '┤' },
    heavy: { left: '┣', right: '┫' },
    rounded: { left: '├', right: '┤' },
  }
  const conn = connectors[style]
  return conn.left + chars.horizontal.repeat(width) + conn.right
}

/**
 * Create a complete ASCII box with content
 *
 * @example
 * ```ts
 * const box = createBox({
 *   width: 30,
 *   style: 'double',
 *   padding: 1,
 *   align: 'center'
 * }, [
 *   'Welcome to PersonaOS',
 *   '',
 *   'Press any key to continue'
 * ])
 * ```
 */
export function createBox(options: BoxOptions, lines: string[]): string {
  const { width, style = 'double', padding = 0, align = 'left' } = options
  const innerWidth = width

  const result: string[] = []

  // Top border
  result.push(boxTop(innerWidth, style))

  // Top padding
  for (let i = 0; i < padding; i++) {
    result.push(boxEmptyRow(innerWidth, style))
  }

  // Content lines
  for (const line of lines) {
    result.push(boxRow(line, innerWidth, style, align))
  }

  // Bottom padding
  for (let i = 0; i < padding; i++) {
    result.push(boxEmptyRow(innerWidth, style))
  }

  // Bottom border
  result.push(boxBottom(innerWidth, style))

  return result.join('\n')
}

/**
 * Create a message box (commonly used for dialogs)
 */
export function messageBox(title: string, messages: string[], width = 40): string {
  const lines: string[] = []

  // Empty line
  lines.push('')

  // Title (centered)
  if (title) {
    lines.push(title)
    lines.push('')
  }

  // Messages
  for (const msg of messages) {
    lines.push(msg)
  }

  // Empty line at end
  lines.push('')

  return createBox({ width, style: 'double', align: 'center' }, lines)
}

/**
 * Create an alert/notification box
 */
export function alertBox(message: string, width = 40): string {
  return createBox(
    { width, style: 'double', padding: 1, align: 'center' },
    [message]
  )
}

/**
 * Create a success message box
 */
export function successBox(title: string, messages: string[], width = 40): string {
  const lines = [
    '',
    title,
    '',
    ...messages,
    ''
  ]
  return createBox({ width: width - 2, style: 'double', align: 'center' }, lines)
}

/**
 * Create an error message box
 */
export function errorBox(message: string, width = 40): string {
  const lines = [
    '',
    '! ERROR !',
    '',
    message,
    ''
  ]
  return createBox({ width: width - 2, style: 'double', align: 'center' }, lines)
}

/**
 * Create an info panel with a header
 */
export function infoPanel(header: string, content: string[], width = 50): string {
  const lines: string[] = []
  const innerWidth = width - 2

  // Header section
  lines.push(boxTop(innerWidth, 'double'))
  lines.push(boxRow(header, innerWidth, 'double', 'center'))
  lines.push(boxDivider(innerWidth, 'double'))

  // Content
  for (const line of content) {
    lines.push(boxRow(line, innerWidth, 'double', 'left'))
  }

  lines.push(boxBottom(innerWidth, 'double'))

  return lines.join('\n')
}

/**
 * Create a window frame with title bar
 */
export function windowFrame(title: string, width: number, height: number, style: BoxStyle = 'double'): string {
  const chars = BoxChars[style]
  const innerWidth = width - 2
  const lines: string[] = []

  // Title bar
  const titleText = ` ${title} `
  const paddingNeeded = innerWidth - titleText.length
  const leftPad = Math.floor(paddingNeeded / 2)
  const rightPad = paddingNeeded - leftPad

  lines.push(chars.topLeft + chars.horizontal.repeat(leftPad) + titleText + chars.horizontal.repeat(rightPad) + chars.topRight)

  // Window content area
  for (let i = 0; i < height - 2; i++) {
    lines.push(chars.vertical + ' '.repeat(innerWidth) + chars.vertical)
  }

  // Bottom
  lines.push(chars.bottomLeft + chars.horizontal.repeat(innerWidth) + chars.bottomRight)

  return lines.join('\n')
}

/**
 * Wrap text to fit within a given width
 */
export function wrapText(text: string, width: number): string[] {
  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    if (currentLine.length + word.length + 1 <= width) {
      currentLine += (currentLine ? ' ' : '') + word
    } else {
      if (currentLine) lines.push(currentLine)
      currentLine = word
    }
  }

  if (currentLine) lines.push(currentLine)

  return lines
}
